import Logo from '@/components/logo';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
    Sidebar,
    SidebarContent,
    SidebarFooter,
    SidebarGroup,
    SidebarGroupContent,
    SidebarHeader,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarRail,
    useSidebar,
} from '@/components/ui/sidebar';
import { useAuthContext } from '@/context/auth-provider';
import { useTheme } from '@/context/theme-provider';
import {
    EllipsisIcon,
    KeyIcon,
    Loader,
    Lock,
    LogOut,
    MoonStarIcon,
    Settings,
    SidebarCloseIcon,
    SidebarOpenIcon,
    SunIcon,
    User,
} from 'lucide-react';
import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import LogoutDialog from './logout-dialog';

const Asidebar = () => {
    const { theme, setTheme } = useTheme();
    const { isLoading, user } = useAuthContext();
    const { pathname } = useLocation();

    const { open, toggleSidebar } = useSidebar();
    const [isOpen, setIsOpen] = useState(false);

    const items = [
        {
            title: 'MFA',
            tooltip: 'Multi-Factor Authentication',
            url: '/mfa',
            icon: KeyIcon,
        },

        {
            title: 'Sessions',
            tooltip: 'See all sessions',
            url: '/sessions',
            icon: Lock,
        },

        {
            title: 'Account',
            tooltip: 'Account Informations',
            url: '/account',
            icon: User,
        },

        {
            title: 'Settings',
            tooltip: 'Account Settings',
            url: '/settings',
            icon: Settings,
        },
    ];

    return (
        <>
            <Sidebar collapsible='icon'>
                <SidebarHeader className='dark:bg-background'>
                    <div
                        className={`flex flex-col ${open ? 'h-[100px]' : 'h-[80px]'} items-center py-4`}>
                        <Logo
                            size='60px'
                            url='/mfa'
                        />
                        {open && (
                            <Link
                                to='/mfa'
                                className='hidden md:flex ml-2 text-[12px] tracking-[-0.16px] text-black dark:text-[#fcfdffef] font-bold mb-0'>
                                Advanced Sessions Management
                            </Link>
                        )}
                    </div>
                </SidebarHeader>
                <SidebarContent
                    className='dark:bg-background'
                    aria-describedby='Sidebar menu items'>
                    <SidebarGroup className='px-0'>
                        <SidebarGroupContent aria-describedby='Sidebar menu items'>
                            <SidebarMenu className=''>
                                {items.map((item) => (
                                    <SidebarMenuItem
                                        key={item.title}
                                        title={open ? undefined : item.tooltip}
                                        className={`${item.url === pathname ? 'bg-sidebar-foreground/20' : ''}`}>
                                        <SidebarMenuButton
                                            asChild
                                            className='pl-4'>
                                            <Link
                                                to={item.url}
                                                className='!text-sm outline-none rounded-none group-data-[collapsible=icon]:!w-full group-data-[collapsible=icon]:!pl-4'>
                                                <item.icon />
                                                <span>{item.title}</span>
                                            </Link>
                                        </SidebarMenuButton>
                                    </SidebarMenuItem>
                                ))}
                            </SidebarMenu>
                        </SidebarGroupContent>
                    </SidebarGroup>
                </SidebarContent>
                <SidebarFooter className='dark:bg-background'>
                    <SidebarMenu className='hidden md:block'>
                        <SidebarMenuItem onClick={toggleSidebar}>
                            <SidebarMenuButton
                                size='lg'
                                className={`${open ? '!px-[1rem]' : '!px-[1.5rem]'}`}
                                onClick={toggleSidebar}
                                asChild>
                                <div
                                    className='!text-sm outline-none group-data-[collapsible=icon]:!w-full group-data-[collapsible=icon]:!p-[5.5px] rounded-md cursor-pointer group-data-[collapsible=icon]:!flex group-data-[collapsible=icon]:!items-center'
                                    title={
                                        open
                                            ? 'Collapse Sidebar'
                                            : 'Expand Sidebar'
                                    }>
                                    {open ? (
                                        <SidebarCloseIcon className='!size-5' />
                                    ) : (
                                        <SidebarOpenIcon className='!size-5' />
                                    )}
                                    <span>Collapse</span>
                                </div>
                            </SidebarMenuButton>
                        </SidebarMenuItem>
                    </SidebarMenu>
                    <SidebarMenu>
                        <SidebarMenuItem>
                            {isLoading ? (
                                <Loader
                                    size='24px'
                                    className='place-self-center self-center animate-spin'
                                />
                            ) : (
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <SidebarMenuButton
                                            size='lg'
                                            className='data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground'>
                                            <Avatar className='h-8 w-8 rounded-full'>
                                                <AvatarFallback className='rounded-full border border-gray-500'>
                                                    {user?.name
                                                        ?.split(' ')?.[0]
                                                        ?.charAt(0)}
                                                    {user?.name
                                                        ?.split(' ')?.[1]
                                                        ?.charAt(0)}
                                                </AvatarFallback>
                                            </Avatar>
                                            <div className='grid flex-1 text-left text-sm leading-tight'>
                                                <span className='truncate font-semibold'>
                                                    {user?.name}
                                                </span>
                                                <span className='truncate text-xs'>
                                                    {user?.email}
                                                </span>
                                            </div>
                                            <EllipsisIcon className='ml-auto size-4' />
                                        </SidebarMenuButton>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent
                                        className='w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg'
                                        side={'bottom'}
                                        align='start'
                                        sideOffset={4}>
                                        <DropdownMenuGroup>
                                            <DropdownMenuItem
                                                onClick={() =>
                                                    setTheme(
                                                        theme === 'light'
                                                            ? 'dark'
                                                            : 'light',
                                                    )
                                                }>
                                                {theme === 'dark' ? (
                                                    <MoonStarIcon />
                                                ) : (
                                                    <SunIcon />
                                                )}
                                                Toggle theme
                                            </DropdownMenuItem>
                                        </DropdownMenuGroup>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem
                                            onClick={() => setIsOpen(true)}>
                                            <LogOut />
                                            Log out
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            )}
                        </SidebarMenuItem>
                    </SidebarMenu>
                </SidebarFooter>
                <SidebarRail />
            </Sidebar>

            <LogoutDialog
                isOpen={isOpen}
                setIsOpen={setIsOpen}
            />
        </>
    );
};

export default Asidebar;
